// Copyright (c) Microsoft. All rights reserved.

import { FC, useState } from 'react';

import {
    Button,
    Divider,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { useChat } from '../../../../libs/hooks';
import { useAppSelector } from '../../../../redux/app/hooks';
import { RootState } from '../../../../redux/app/store';
import { FeatureKeys } from '../../../../redux/features/app/AppState';
import { InvitationJoinDialog } from '../../invitation-dialog/InvitationJoinDialog';
import { Breakpoints, customTokens } from '../../../../styles';
import { BotAdd20Regular, BotAdd24Regular } from '@fluentui/react-icons';

const useClasses = makeStyles({
    menuWrapper: {
        width: '100%',
        ...Breakpoints.small({
            width: 'auto',
            paddingLeft: '10px',
        }),
    },
    newButtonWrapper: {
        ...shorthands.border('1px', 'solid', 'rgb(189, 189, 189)'),
        ...shorthands.padding('4px', '12px'),
        ...shorthands.borderRadius('4px'),
        cursor: 'pointer',
        ...Breakpoints.small({
            paddingRight: '45px',
            paddingLeft: '45px',
            display: 'flex',
        }),
    },
    newButton: {
        ...shorthands.padding('0'),
        ...shorthands.outline('none'),
        width: '100%',
        '& .fui-Button__icon': {
            height: '20px',
            width: '20px',
        },
    },
    newSmallButtonWrapper: {
        ...shorthands.padding('0', '2px', '0', '1px'),
    },
    newSmallButton: {
        maxWidth: '25px',
        minWidth: '25px',
        '& .fui-Button__icon': {
            height: '24px',
            width: '24px',
        },
    },
});

interface SimplifiedNewBotMenuProps {
    onFileUpload: () => void;
    onCreateNewChat?: () => void;
    title?: string;
    isSmallButton?: boolean;
}

export const SimplifiedNewBotMenu: FC<SimplifiedNewBotMenuProps> = ({
    onFileUpload,
    onCreateNewChat,
    title,
    isSmallButton,
}) => {
    const classes = useClasses();
    const chat = useChat();
    const { features } = useAppSelector((state: RootState) => state.app);

    // It needs to keep the menu open to keep the FileUploader reference
    // when the file uploader is clicked.
    const [isJoiningBot, setIsJoiningBot] = useState(false);

    const onAddChat = () => {
        void chat.createChat();
        if (onCreateNewChat) {
            onCreateNewChat();
        }
    };
    const onJoinClick = () => {
        setIsJoiningBot(true);
    };

    const onCloseDialog = () => {
        setIsJoiningBot(false);
    };

    return (
        <div className={classes.menuWrapper}>
            <Menu>
                <MenuTrigger disableButtonEnhancement>
                    <div
                        className={
                            isSmallButton
                                ? mergeClasses(classes.newButtonWrapper, classes.newSmallButtonWrapper)
                                : classes.newButtonWrapper
                        }
                    >
                        <Button
                            data-testid="createNewConversationButton"
                            icon={
                                isSmallButton ? (
                                    <BotAdd24Regular color={customTokens.ekfbTeal} />
                                ) : (
                                    <BotAdd20Regular color={customTokens.ekfbTeal} />
                                )
                            }
                            appearance="transparent"
                            className={
                                isSmallButton
                                    ? mergeClasses(classes.newButton, classes.newSmallButton)
                                    : classes.newButton
                            }
                        >
                            {title ?? ''}
                        </Button>
                    </div>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem data-testid="addNewBotMenuItem" onClick={onAddChat}>
                            New Chat Session
                        </MenuItem>
                        {features[FeatureKeys.BotAsDocs].enabled && (
                            <>
                                <Divider />
                                <MenuItem
                                    data-testid="uploadABotMenuItem"
                                    disabled={!features[FeatureKeys.BotAsDocs].enabled}
                                    // TODO: [Issue #47] Fix, not sure if it works right
                                    onClick={onFileUpload}
                                >
                                    <div>Upload Saved Chat</div>
                                </MenuItem>
                            </>
                        )}
                        {features[FeatureKeys.MultiUserChat].enabled && (
                            <MenuItem
                                data-testid="joinABotMenuItem"
                                disabled={!features[FeatureKeys.MultiUserChat].enabled}
                                onClick={onJoinClick}
                            >
                                Join Shared Chat
                            </MenuItem>
                        )}
                    </MenuList>
                </MenuPopover>
            </Menu>
            {isJoiningBot && <InvitationJoinDialog onCloseDialog={onCloseDialog} />}
        </div>
    );
};
